import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { adminGetPatterns, adminDetectPatterns, adminDetectStatus } from "@/lib/apiClient";
import AdminNav from "@/components/AdminNav";
import { toast } from "sonner";

const STATUS_TABS = ["candidate", "draft", "published", "rejected", "merged"];
const badge = { candidate: "bg-bryellow text-ink", draft: "bg-secondary text-ink/70", published: "bg-brlime text-ink", rejected: "bg-brorange text-white", merged: "bg-ink text-white" };

export default function AdminPatterns() {
  const [tab, setTab] = useState("candidate");
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => { adminGetPatterns(tab).then(setItems); }, [tab]);
  useEffect(load, [load]);

  const pollStatus = useCallback(() => {
    adminDetectStatus().then((s) => {
      setRunning(s.running);
      if (!s.running && timer.current) { clearInterval(timer.current); timer.current = null; load(); toast.success("Pattern detection finished"); }
    });
  }, [load]);

  useEffect(() => { adminDetectStatus().then((s) => { setRunning(s.running); if (s.running) startPoll(); }); }, []);
  const startPoll = () => { if (!timer.current) timer.current = setInterval(pollStatus, 4000); };

  const detect = async () => {
    const r = await adminDetectPatterns();
    if (r.started) { setRunning(true); startPoll(); toast.success("Detecting patterns from published cases… this runs in the background"); }
    else toast.message(r.message || "Already running");
  };

  return (
    <div>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow text-ink/60">Evidence-led</p>
            <h1 className="font-display font-extrabold text-4xl display-tight uppercase">Patterns</h1>
            <p className="text-sm text-ink/50 mt-1">Repeated business changes detected across published cases. Review before publishing.</p>
          </div>
          <button onClick={detect} disabled={running} data-testid="detect-patterns-button" className="group btn-tactile inline-flex items-center gap-2 bg-ink text-white font-bold px-5 h-11 hover:bg-cobalt disabled:opacity-60">
            <Sparkles size={18} /> {running ? "Detecting…" : "Detect Patterns"}
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mt-8">
          {STATUS_TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`pattern-tab-${t}`} className={`btn-tactile px-4 h-9 border-2 border-ink font-semibold text-sm capitalize ${tab === t ? "bg-ink text-white" : "bg-white hover:bg-bryellow"}`}>{t}</button>
          ))}
        </div>

        <div className="mt-6 space-y-3" data-testid="admin-patterns-list">
          {items.length === 0 ? (
            <div className="border-2 border-ink p-10 text-center text-ink/50">No {tab} patterns. {tab === "candidate" && "Click Detect Patterns to analyse your published cases."}</div>
          ) : items.map((p) => (
            <Link key={p.id} to={`/admin/patterns/${p.id}`} data-testid={`admin-pattern-${p.id}`} className="group block border-2 border-ink p-5 bg-white hover:shadow-[6px_6px_0_0_#0A0A0C]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`eyebrow px-2 py-0.5 ${badge[p.status] || "bg-secondary"}`}>{p.status}</span>
                    <h3 className="font-display font-extrabold text-xl group-hover:text-cobalt transition-colors">{p.name}</h3>
                  </div>
                  <p className="text-sm text-ink/60 mt-1 line-clamp-1">{p.description}</p>
                  <p className="text-xs text-ink/50 mt-1">{p.supporting_count} cases · {(p.industries || []).length} industries · {(p.countries || []).length} countries</p>
                </div>
                <ArrowRight size={18} className="arrow-move shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
